// SignerEmailSwap — the universal "Change email" affordance for one signer.
//
// One little inline pattern everywhere a signer roster is shown: a small ghost
// trigger that swaps to an inline email field with Save / Cancel. The MUTATION
// is site-provided (`onSubmit`) so the same component serves:
//   • signature-record signers (PATCH /api/lease-drafts/:groupId/signers/:role,
//     doc-type-aware via leaseDraftsClient.updateSignerEmail), and
//   • pre-DocuSeal owner-intake owners (PATCH /api/owner-intake/:groupId/
//     owners/:ownerKey/email via ownerIntakeClient.swapOwnerIntakeOwnerEmail).
//
// The component owns only the UI state (editing / draft / busy / inline error).
// On success it exits editing and calls onSaved — the site refetches + toasts.
// Rendering contract: NOT editing → a single small button (fits inline in a
// flex roster row); editing → a block-level `.field` (give the row
// flexWrap:'wrap' and the field flex:'1 1 100%' wraps onto its own line).

import { useRef, useState } from 'react';
import { ApiError } from '@/lib/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The server's human message off an ApiError body, else a fallback. */
export function emailSwapError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.error ?? body?.message ?? `${fallback} (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : fallback;
}

export function SignerEmailSwap({
  inputId,
  signerName,
  currentEmail,
  disabled = false,
  triggerLabel = 'Change email',
  onSubmit,
  onSaved,
}: {
  /** Unique id for the email input (label association). */
  inputId: string;
  /** Display name — used in the field label ("Email for {name}"). */
  signerName: string;
  /** The address on record now — seeds the draft; Save requires a change. */
  currentEmail: string;
  disabled?: boolean;
  triggerLabel?: string;
  /** The mutation. Throw to keep the editor open with an inline error. */
  onSubmit: (email: string) => Promise<void>;
  /** Called after a successful save (site refetches + toasts). */
  onSaved?: () => void;
}): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const openEditor = () => {
    setDraft(currentEmail);
    setError(null);
    setEditing(true);
    // Focus after the input renders.
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const close = () => {
    if (busy) return;
    setEditing(false);
    setError(null);
  };

  const emailValid = EMAIL_RE.test(draft.trim());
  const emailChanged = draft.trim() !== currentEmail.trim();

  const save = async () => {
    if (!emailValid) return;
    if (!emailChanged) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(draft.trim());
      setEditing(false);
      onSaved?.();
    } catch (err) {
      setError(emailSwapError(err, "Couldn't update the email"));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="btn"
        data-variant="ghost"
        data-size="sm"
        disabled={disabled}
        onClick={openEditor}
      >
        {triggerLabel}
      </button>
    );
  }

  return (
    <div className="field" style={{ flex: '1 1 100%', margin: 0 }}>
      <label htmlFor={inputId} className="label">
        Email for {signerName}
      </label>
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="email"
          className="input"
          style={{ flex: '1 1 220px', minWidth: 0 }}
          value={draft}
          aria-invalid={draft.length > 0 && !emailValid}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            } else if (e.key === 'Escape') {
              // Don't let a modal-level Escape also fire.
              e.preventDefault();
              e.stopPropagation();
              close();
            }
          }}
        />
        <button
          type="button"
          className="btn"
          data-variant="primary"
          data-size="sm"
          disabled={busy || !emailValid || !emailChanged}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="btn"
          data-variant="ghost"
          data-size="sm"
          disabled={busy}
          onClick={close}
        >
          Cancel
        </button>
      </div>
      {draft.length > 0 && !emailValid && (
        <div className="helper" data-state="error">
          Enter a valid email address.
        </div>
      )}
      {error && (
        <div className="helper" data-state="error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
